import { useState, Fragment } from "react";
import SunEditor from "suneditor-react";
import "suneditor/dist/css/suneditor.min.css"; // Import Sun Editor's CSS File
import {
  Box,
  Button,
  Grid,
  Typography,
  FormControl,
  FormControlLabel,
  MenuItem,
  InputLabel,
  Hidden,
  IconButton,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  TextField,
  Select,
  Slider,
  Checkbox,
  Switch,
  FormHelperText,
  Tabs,
  Tab,
  FormLabel,
  RadioGroup,
  Radio,
  Dialog,
  InputAdornment,
  Input,
  Divider
} from "@mui/material";
import { useCustomSnackbar } from "../components/CustomSnackbar";
import { Waiting } from "./Waiting";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SnippetFolderIcon from "@mui/icons-material/SnippetFolder";
import DescriptionIcon from "@mui/icons-material/Description";
import SelectAssetForm from "../views/HiringManager/SelectAsset";
import ConfirmationForm from "./ConfirmationForm";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import {
  GET_TEST_APPS,
  GET_TEST_ASSETS,
  GET_TEST_TEMPLATE,
} from "../graphql/queries";
import { useQuery } from "@apollo/client";
import useGuardedRoute from "../hooks/useGuardedRoute";
import AddAssetForm from "../views/HiringManager/AddAsset";
import { useAppContext } from "../context/AppContext";
import TestTemplateFileBrowser from "./TestTemplateFileBrowser";

export default function TestTemplateForm(props) {
  const { store } = useAppContext();
  const { t, i18n } = useTranslation();
  const { lsEnqueueSnackbar } = useCustomSnackbar();
  const [isSelectAssetOpen, setIsSelectAssetOpen] = useState(false);
  const [isUploadInstructionsOpen, setIsUploadInstructionsOpen] =
    useState(false);
  const [currentCategory, setCurrentCategory] = useState("0");
  const [selectedItem, setSelectedItem] = useState(null);
  const [confirmChangeTestPlatform, setConfirmChangeTestPlatform] = useState(false);
  const [newTestPlatform, setNewTestPlatform] = useState(store.currentTestTemplate?.testPlatform);


  // Retrieve test applications
  const { data: taData, loading: taLoading } = useQuery(GET_TEST_APPS, {
    variables: {
      orgId: store.currentOrg.org.id,
    },
    onError: (e) => {
      lsEnqueueSnackbar(e.message, {
        variant: "error",
      });
    },
  });

  // Retrieve test data
  const { data: ttData, loading: ttLoading } = useQuery(GET_TEST_TEMPLATE, {
    variables: {
      orgId: store.currentOrg.org.id,
      templateId: store.currentTestTemplate?.id,
    },
    onError: (e) => {
      lsEnqueueSnackbar(e.message, {
        variant: "error",
      });
    },
  });

  // Retrieve all assets
  const { data: asData, loading: asLoading } = useQuery(GET_TEST_ASSETS, {
    variables: {
      orgId: store.currentOrg.org.id,
      folderId: null,
      assetStatus: [],
    },
    onError: (e) => {
      lsEnqueueSnackbar(e.message, {
        variant: "error",
      });
    },
  });

  useGuardedRoute("/app", [store.currentTestTemplate]);

  const handleOverallScoreRangeChange = (event, newValue) => {
    formik.setFieldValue("overallScoreRange", newValue);
    let overallScoreDetails = [...formik.values.overallScoreDetails];
    let rows = Array.from(
      { length: (newValue[1] - newValue[0]) / 1 + 1 },
      (_, i) => newValue[0] + i * 1
    );
    rows.forEach((r) => {
      if (!overallScoreDetails.find((d) => d.score === r)) {
        overallScoreDetails.push({
          score: r,
          name: "",
          description: "",
        });
      }
    });
    let newOverallScoreDetails = overallScoreDetails
      .filter(function (scores_el) {
        return (
          rows.filter(function (rows_el) {
            return rows_el === scores_el.score;
          }).length > 0
        );
      })
      .sort((a, b) => a.score - b.score);
    formik.setFieldValue("overallScoreDetails", newOverallScoreDetails);
  };

  const handleCategoryScoreRangeChange = (event, newValue) => {
    formik.setFieldValue(`categories.${currentCategory}.scoreRange`, newValue);
    let categoryScoreDetails = [
      ...formik.values.categories[currentCategory].categoryScoreDetails,
    ];
    let rows = Array.from(
      { length: (newValue[1] - newValue[0]) / 1 + 1 },
      (_, i) => newValue[0] + i * 1
    );
    rows.forEach((r) => {
      if (!categoryScoreDetails.find((d) => d.score === r)) {
        categoryScoreDetails.push({
          score: r,
          name: "",
          description: "",
        });
      }
    });
    let newCategoryScoreDetails = categoryScoreDetails
      .filter(function (scores_el) {
        return (
          rows.filter(function (rows_el) {
            return rows_el === scores_el.score;
          }).length > 0
        );
      })
      .sort((a, b) => a.score - b.score);
    formik.setFieldValue(
      `categories.${currentCategory}.categoryScoreDetails`,
      newCategoryScoreDetails
    );
  };

  const handleOpenSelectAsset = () => {
    setIsSelectAssetOpen(true);
  };

  const handleOpenUploadInstructions = () => {
    setIsUploadInstructionsOpen(true);
  };

  const handleInstructionsUploadSuccess = (asset) => {
    formik.setFieldValue("instructions.assetId", asset.id);
  };

  const handleSelectedAssets = (assetList) => {
    const formAssets = assetList.map((asset) => ({
      id: asset.id,
      asset,
      targetPath: selectedItem?.path || "",
      isFolder: asset.isFolder,
    }));
    if (formik.values.testPlatform === 'appstream') {
      formik.setFieldValue("inputs", [...formik.values.inputs, ...formAssets]);
    } else if (formik.values.testPlatform === 'website') {
      formik.setFieldValue("inputs", formAssets);
    }
  };

  function handleInputUpdate(newInputList, newEmptyFolderList) {
    formik.setFieldValue("inputs", newInputList);
    formik.setFieldValue("inputEmptyFolders", newEmptyFolderList);
  }

  const handleAddOutput = () => {
    const outputs = [...formik.values.outputs];
    outputs.push({
      sequence: "",
      name: "",
      extension: "",
      autoSelect: false,
      autoSelectPath: "",
    });

    formik.setFieldValue("outputs", outputs);
  };

  const handleRemoveOutput = (index) => {
    const outputs = [...formik.values.outputs];
    outputs.splice(index, 1);
    // if (formik.errors.outputs) {
    //   const newOutputErrors = [...formik.errors.outputs];
    //   newOutputErrors.splice(index, 1);
    // }
    formik.setFieldValue("outputs", outputs);
  };

  const handleAddCategory = () => {
    const categories = [...formik.values.categories];
    categories.push({
      sequence: "",
      name: "",
      description: "",
      scoreRange: [0, 1],
      weight: "",
      categoryScoreDetails: [
        {
          score: 0,
          name: "",
          description: "",
        },
        {
          score: 1,
          name: "",
          description: "",
        },
      ],
    });

    formik.setFieldValue("categories", categories);
  };

  const handleRemoveCategory = (index) => {
    const categories = [...formik.values.categories];
    categories.splice(index, 1);
    // if (formik.errors.categories) {
    //   const newCategoryErrors = [...formik.errors.categories];
    //   newCategoryErrors.splice(index, 1);
    // }
    formik.setFieldValue("categories", categories);
  };

  const handleInstructionTextChange = (content) => {
    formik.setFieldValue("instructions.text", content);
  };

  const handleAppSelectCheckboxChange = (event, app) => {
    if (event.target.checked) {
      formik.setFieldValue('apps', [...formik.values.apps, { ...app, autoStart: false }]);
    } else {
      formik.setFieldValue(
        'apps',
        formik.values.apps.filter(a => a.id !== app.id)
      );
    }
  };

  const handleAutoStartChange = (event, app) => {
    //There can only be one autoStart app selected. Automatically sets all others to false
    const updatedApps = formik.values.apps.map(a => {
      if (a.id === app.id) {
        return { ...a, autoStart: event.target.checked };
      } else {
        return { ...a, autoStart: false };
      }
    });
    formik.setFieldValue('apps', updatedApps);
  };

  const handleChangeTestPlatform = (event) => {
    let testPlatform = event.target.value;
    if (testPlatform !== newTestPlatform) {
      setNewTestPlatform(testPlatform);
      setConfirmChangeTestPlatform(true);
    }
  };

  const handleConfirmChangePlatformClose = (response) => {
    setConfirmChangeTestPlatform(false);
    if (response === 'Confirm') {
      // wipe out the apps, inputs and outputs
      formik.setFieldValue('apps', []);
      formik.setFieldValue('inputs', []);
      formik.setFieldValue('inputEmptyFolders', []);
      formik.setFieldValue('outputs', [
        {
          sequence: 1,
          name: "",
          extension: "",
          autoSelect: false,
          autoSelectPath: "",
        },
      ]);
      formik.setFieldValue('allowDownload', false);
      formik.setFieldValue('allowUpload', false);
      formik.setFieldValue('testPlatform', newTestPlatform);
    } else {
      setNewTestPlatform(formik.values.testPlatform);
    }
  };

  let testTemplateData = ttData?.getTestTemplate || [];
  let appsData = [];
  let instructionsData = [];
  let pageResults;

  const validationSchema = Yup.object({
    name: Yup.string().required(t("Required")),
    outputs: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().required(t("Required")),
        extension: Yup.string().required(t("Required")),
        autoSelect: Yup.boolean(),
        autoSelectPath: Yup.string().when('autoSelect', {
          is: (autoSelect) => autoSelect === true,
          then: Yup.string().required(t("Required")),
          otherwise: Yup.string().notRequired()
        })
      })
    ),
    overallScoreDetails: Yup.array().of(
      Yup.object().shape({
        description: Yup.string().when("overallScoreDetails", {
          is: (overallScoreDetails) => overallScoreDetails === true,
          then: Yup.string().required(t("Required"))
        })
      })
    ),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: props.isCopyMode
        ? ""
        : testTemplateData.name
          ? testTemplateData.name.trim()
          : "",
      description: testTemplateData.description
        ? testTemplateData.description.trim()
        : "",
      testPlatform: testTemplateData.testPlatform || "appstream",
      maxWorkTimeHours: testTemplateData.maxWorkTime
        ? Math.floor(testTemplateData.maxWorkTime / 60 / 60) || ""
        : "",
      maxWorkTimeMinutes: testTemplateData.maxWorkTime
        ? (testTemplateData.maxWorkTime / 60) % 60 || ""
        : "",
      allowDownload: testTemplateData.allowDownload ?? false,
      allowUpload: testTemplateData.allowUpload ?? false,
      allowPause: testTemplateData.allowPause ?? true,
      instructions: testTemplateData?.instructions
        ? {
          isText: testTemplateData.instructions.type === "text",
          assetId: testTemplateData.instructions.asset?.id || "",
          text: testTemplateData.instructions.text || "",
        }
        : {
          isText: false,
          assetId: "",
          text: "",
        },
      apps: testTemplateData.apps
        ? testTemplateData.apps
        : [],
      inputs: testTemplateData?.inputs?.length
        ? testTemplateData.inputs
          .filter((inp) => inp.asset)
          .map((inp) => ({
            asset: inp.asset,
            id: inp.id,
            targetPath: inp.targetPath || "",
          }))
        : [],
      inputEmptyFolders: testTemplateData?.inputs?.length
        ? testTemplateData.inputs
          .filter((inp) => !inp.asset)
          .map((inp) => ({
            asset: null,
            id: inp.id,
            targetPath: inp.targetPath || "",
          }))
        : [],
      outputs:
        testTemplateData.outputs && testTemplateData.outputs.length > 0
          ? testTemplateData.outputs.map(output => ({
            ...output,
            autoSelect: output.autoSelectPath ? true : false,
            // Remove /Test Files/ from the outputs, it will get readded during submission
            autoSelectPath: output.autoSelectPath ? output.autoSelectPath.substring(12) : "",
          }))
          : [
            {
              sequence: 1,
              name: "",
              extension: "",
              autoSelect: false,
              autoSelectPath: "",
            },
          ],
      hasOverallScore: testTemplateData.rubric?.hasOverallScore || false,
      hasCategoryScore: testTemplateData.rubric?.hasCategoryScore || false,
      overallScoreRange: testTemplateData.rubric?.overallScoreRange
        ? [
          testTemplateData.rubric.overallScoreRange.low,
          testTemplateData.rubric.overallScoreRange.high,
        ]
        : [0, 1],
      overallScoreDetails:
        testTemplateData.rubric?.overallScoreDetails &&
          testTemplateData.rubric?.overallScoreDetails.length > 0
          ? testTemplateData.rubric.overallScoreDetails.map((osd) => ({
            score: osd.score,
            name: osd.name,
            description: osd.description,
          }))
          : [
            {
              score: 0,
              name: "",
              description: "",
            },
            {
              score: 1,
              name: "",
              description: "",
            },
          ],
      categories:
        testTemplateData.rubric?.categories &&
          testTemplateData.rubric?.categories.length > 0
          ? testTemplateData.rubric.categories.map((category) => ({
            sequence: category.sequence,
            name: category.name,
            description: category.description,
            scoreRange: [category.scoreRange.low, category.scoreRange.high],
            weight: category.weight,
            categoryScoreDetails: category.scoreDetails.map((sd) => ({
              score: sd.score,
              name: sd.name,
              description: sd.description,
            })),
          }))
          : [
            {
              sequence: 1,
              name: "",
              description: "",
              scoreRange: [0, 1],
              weight: "",
              categoryScoreDetails: [
                {
                  score: 0,
                  name: "",
                  description: "",
                },
                {
                  score: 1,
                  name: "",
                  description: "",
                },
              ],
            },
          ],
    },
    validationSchema: validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      let inputs = [...(values.inputs.map(({ asset, targetPath }) => ({
        assetId: asset.id,
        targetPath,
      }))), ...(values.inputEmptyFolders.map((item) => ({
        assetId: null,
        targetPath: item.targetPath || item.fullpath
      })))];

      let outputs = [];
      for (let o of values.outputs) {
        if (o.name !== "" && o.extension !== "") {
          outputs.push({
            sequence: values.outputs.indexOf(o),
            name: o.name,
            extension: o.extension,
            isFolder: o.extension === "directory" ? true : false,
            //Add back the Test Files prefix
            autoSelectPath: o.autoSelectPath ? `/Test Files/${o.autoSelectPath}` : null,
          });
        }
      }

      let categories = [];
      for (let c of values.categories) {
        if (c.name !== "") {
          categories.push({
            sequence: values.categories.indexOf(c),
            name: c.name,
            description: c.description,
            scoreRange: {
              low: Number(c.scoreRange[0]),
              high: Number(c.scoreRange[1]),
            },
            weight: Number(c.weight),
            scoreDetails: c.categoryScoreDetails,
          });
        }
      }

      let overallScoreDetails = [];
      for (let s of values.overallScoreDetails) {
        if (s.score !== "") {
          overallScoreDetails.push({
            score: s.score,
            name: s.name,
            description: s.description,
          });
        }
      }

      const instructions = {
        type: values.instructions.isText ? "text" : "asset",
        text: values.instructions.text,
        assetId: values.instructions.assetId,
      };
      // text is null if the type is asset, else it has to be length > 0 (validation)
      if (!values.instructions.isText) {
        instructions.text = null;
      }
      // assetId is null if it's falsy value or the type is text
      if (!values.instructions.assetId || values.instructions.isText) {
        instructions.assetId = null;
      }

      const vars = {
        variables: {
          orgId: store.currentOrg.org.id,
          testTemplate: {
            id: testTemplateData.id,
            name: values.name.trim(),
            description:
              values.description === "" ? null : values.description.trim(),
            maxWorkTime:
              Number(values.maxWorkTimeHours) * 60 * 60 +
              Number(values.maxWorkTimeMinutes) * 60,
            allowDownload: values.allowDownload,
            allowUpload: values.allowUpload,
            allowPause: values.allowPause,
            instructions,
            inputs: inputs.length ? inputs : [],
            apps: values.apps.length ? values.apps.map((app) => ({ "id": app.id, "autoStart": app.autoStart })) : null,
            outputs: outputs.length === 0 ? null : outputs,
            rubric: (values.hasOverallScore || values.hasCategoryScore) ? {
              hasOverallScore: values.hasOverallScore,
              hasCategoryScore: values.hasCategoryScore,
              overallScoreRange: {
                low: Number(values.overallScoreRange[0]),
                high: Number(values.overallScoreRange[1]),
              },
              overallScoreDetails:
                overallScoreDetails.length === 0 ? null : overallScoreDetails,
              categories: categories.length === 0 ? null : categories,
            } : null,
            testPlatform: values.testPlatform
          },
        },
      };

      props.handleSubmit(vars, setSubmitting);
    },
  });

  const [activeTab, setActiveTab] = useState(0);

  const handleChangeTab = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleNext1 = () => {
    if (!formik.errors.name) {
      setActiveTab(1);
    }
  };

  const handleNext2 = () => {
    setActiveTab(2);
  };

  const handleNext3 = () => {
    setActiveTab(3);
  };

  const handleNext4 = () => {
    setActiveTab(4);
  };

  const handleNext5 = () => {
    setActiveTab(5);
  };

  const handleRemoveWebsiteInput = () => {
    formik.setFieldValue("inputs", []);
  };

  const handleNext6 = () => {
    // Set all fields in outputs array as touched to trigger validation error messages
    formik.validateField('outputs');
    formik.setTouched({
      ...formik.touched,
      outputs: formik.values.outputs.map((output) => ({
        ...output,
        name: true,
        extension: true,
        autoSelectPath: true,
      }))
    });
    if (!formik.errors.outputs) {
      setActiveTab(6);
    }
  };

  if (taLoading || ttLoading || asLoading) {
    pageResults = <Waiting />;
  } else if (taData && ttData && asData) {
    testTemplateData = ttData.getTestTemplate;

    appsData = taData.getTestApps.slice();
    instructionsData = asData.getTestAssets.slice();

    let outputOptions = [];
    formik.values.apps.forEach((application) => {
      if (application && application.extensions) {
        outputOptions.push(
          application.extensions.map((e, index) => (
            <MenuItem key={application.id + "-" + index} value={e.extension}>
              {e.extension + " - " + e.description}
            </MenuItem>
          ))
        );
      }
    });
    let flattenedOutputOptions = outputOptions.flat(1);
    outputOptions = flattenedOutputOptions
      .filter(
        (value, index, self) =>
          index === self.findIndex((e) => e.props.value === value.props.value)
      )
      .sort((a, b) =>
        a.props.value.toUpperCase().localeCompare(b.props.value.toUpperCase())
      );
    pageResults = (
      <form onSubmit={formik.handleSubmit}>
        <Box sx={{ flexGrow: 1, display: "flex" }}>
          <Grid container>
            <Grid item xs={2}>
              <Tabs
                orientation="vertical"
                value={activeTab}
                onChange={handleChangeTab}
                sx={{
                  borderRight: 1,
                  borderColor: "divider",
                }}
              >
                <Tab
                  label={t("GeneralInformation")}
                  id="tab-0"
                  aria-controls="tabpanel-0"
                  value={0}
                />
                <Tab
                  label={t("BasicSettings")}
                  id="tab-1"
                  aria-controls="tabpanel-1"
                  value={1}
                />
                <Tab
                  label={t("Instructions")}
                  id="tab-2"
                  aria-controls="tabpanel-2"
                  value={2}
                />
                <Tab
                  label={t("Inputs")}
                  id="tab-3"
                  aria-controls="tabpanel-3"
                  value={3}
                />
                <Tab
                  label={t("Applications")}
                  id="tab-4"
                  aria-controls="tabpanel-4"
                  value={4}
                />
                <Tab
                  label={t("OutputsTitle")}
                  id="tab-5"
                  aria-controls="tabpanel-5"
                  value={5}
                />
                <Tab
                  label={t("RubricsTitle")}
                  id="tab-6"
                  aria-controls="tabpanel-6"
                  value={6}
                />
              </Tabs>
            </Grid>
            <Grid item xs={10} sx={{ paddingLeft: "5%", paddingRight: "15%" }}>
              <div
                role="tabpanel"
                hidden={activeTab !== 0}
                id="tabpanel-0"
                aria-labelledby="tab-0"
              >
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      name="name"
                      variant="outlined"
                      required
                      fullWidth
                      id="name"
                      label={t("Name")}
                      value={formik.values.name}
                      onChange={formik.handleChange}
                      error={formik.touched.name && Boolean(formik.errors.name)}
                      helperText={formik.touched.name && formik.errors.name}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      name="description"
                      variant="outlined"
                      multiline
                      fullWidth
                      id="description"
                      label={t("Description")}
                      value={formik.values.description}
                      onChange={formik.handleChange}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormLabel id="test-platform-radio-label">{t("TestPlatform")}</FormLabel>
                    <RadioGroup
                      name="testPlatform"
                      aria-labelledby="test-platform-radio-label"
                      id="testPlatform"
                      onChange={handleChangeTestPlatform}
                      value={formik.values.testPlatform}
                    >
                      <FormControlLabel value="appstream" control={<Radio />} label={t("AppStream")} />
                      <FormControlLabel value="website" control={<Radio />} label={t("WebSite")} />
                    </RadioGroup>
                  </Grid>

                  <Grid item xs={12}>
                    <Grid container justifyContent="flex-end">
                      <Grid item>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleNext1}
                          sx={{ marginTop: 1 }}
                        >
                          {t("Continue")}
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </div>
              <div
                role="tabpanel"
                hidden={activeTab !== 1}
                id="tabpanel-1"
                aria-labelledby="tab-1"
              >
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography component="p">
                      {t("TimeCandidateHasToCompleteTest")}
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      name="maxWorkTimeHours"
                      variant="outlined"
                      fullWidth
                      id="maxWorkTimeHours"
                      label={t("Hours")}
                      value={formik.values.maxWorkTimeHours}
                      onChange={formik.handleChange}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      name="maxWorkTimeMinutes"
                      variant="outlined"
                      fullWidth
                      id="maxWorkTimeMinutes"
                      label={t("Minutes")}
                      value={formik.values.maxWorkTimeMinutes}
                      onChange={formik.handleChange}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography component="p" sx={{ marginTop: 3 }}>
                      {t("OptionsDuringTest")}
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formik.values.allowDownload}
                          onChange={formik.handleChange}
                          name="allowDownload"
                          color="primary"
                          disabled={formik.values.testPlatform === 'website'}
                        />
                      }
                      label={t("DownloadAllowed")}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formik.values.allowUpload}
                          onChange={formik.handleChange}
                          name="allowUpload"
                          color="primary"
                          disabled={formik.values.testPlatform === 'website'}
                        />
                      }
                      label={t("UploadAllowed")}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formik.values.allowPause}
                          onChange={formik.handleChange}
                          name="allowPause"
                          color="primary"
                        />
                      }
                      label={t("PauseAllowed")}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Grid container justifyContent="flex-end">
                      <Grid item>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleNext2}
                          sx={{ marginTop: 1 }}
                        >
                          {t("Continue")}
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </div>
              <div
                role="tabpanel"
                hidden={activeTab !== 2}
                id="tabpanel-2"
                aria-labelledby="tab-2"
              >
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    {t("Asset")}
                    <Switch
                      checked={formik.values.instructions.isText}
                      onChange={formik.handleChange}
                      name="instructions.isText"
                      color="primary"
                    />
                    {t("RichText")}
                  </Grid>

                  {formik.values.instructions.isText ? (
                    <Grid container>
                      <Grid item xs={12}>
                        <SunEditor
                          name="instructions.text"
                          lang={i18n.language === "French" ? "fr" : "en"}
                          onChange={handleInstructionTextChange}
                          defaultValue={formik.values.instructions.text}
                          setDefaultStyle="font-family: Arial; font-size: 16px;"
                          setOptions={{
                            buttonList: [
                              ["undo", "redo"],
                              ["font", "fontSize", "formatBlock"],
                              ["paragraphStyle", "blockquote"],
                              [
                                "bold",
                                "underline",
                                "italic",
                                "strike",
                                "subscript",
                                "superscript",
                              ],
                              ["fontColor", "hiliteColor"],
                              ["removeFormat"],
                              "/", // Line break
                              ["outdent", "indent"],
                              ["align", "horizontalRule", "list", "lineHeight"],
                              ["table", "link", "image"],
                              ["fullScreen", "showBlocks", "codeView"],
                              // ["dir", "dir_ltr", "dir_rtl"],
                            ],
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <br />
                        <Typography variant="body2">
                          {t("RichTextEditorInstructions")}
                        </Typography>
                      </Grid>
                    </Grid>
                  ) : (
                    <>
                      <Grid item xs={10}>
                        <FormControl
                          variant="outlined"
                          sx={{
                            margin: 0,
                            width: "100%",
                            display: "flex",
                            flexWrap: "nowrap",
                          }}
                        >
                          <InputLabel id="instructions-label">
                            {t("Instructions")}
                          </InputLabel>
                          <Select
                            label={t("Instructions")}
                            labelId="instructions-label"
                            id="instructions"
                            name="instructions.assetId"
                            defaultValue=""
                            value={formik.values.instructions.assetId}
                            onChange={formik.handleChange}
                          >
                            {instructionsData.map((i, index) => (
                              <MenuItem key={index} value={i.id}>
                                {i.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={2}>
                        <Button
                          fullWidth={true}
                          style={{ height: "56px" }}
                          variant="outlined"
                          onClick={() => {
                            handleOpenUploadInstructions();
                          }}
                        >
                          {t("UploadInstructions")}
                        </Button>
                      </Grid>
                    </>
                  )}
                  <Grid item xs={12}>
                    <Grid container justifyContent="flex-end">
                      <Grid item>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleNext3}
                          sx={{ marginTop: 1 }}
                        >
                          {t("Continue")}
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </div>
              <div
                role="tabpanel"
                hidden={activeTab !== 3}
                id="tabpanel-3"
                aria-labelledby="tab-3"
              >
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Grid
                      container
                      alignItems="center"
                      justifyContent="flex-end"
                      spacing={2}
                    >
                      <Grid item xs={10}>
                        <Typography component="p" sx={{ marginBottom: 3 }}>
                          {(formik.values.testPlatform === "appstream") ?
                            t("TestAssets") : t("ApplicationInput")}
                        </Typography>
                      </Grid>

                      <Grid item xs={2}>
                        <Grid container justifyContent="flex-end">
                          <Grid item>
                            <Hidden xsDown>
                              <Button
                                variant="outlined"
                                onClick={() => {
                                  handleOpenSelectAsset();
                                }}
                              >
                                {t("SelectAsset")}
                              </Button>
                            </Hidden>
                            <Hidden smUp>
                              <IconButton
                                onClick={() => {
                                  handleOpenSelectAsset();
                                }}
                              >
                                <AddIcon />
                              </IconButton>
                            </Hidden>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid item xs={12}>
                      {formik.values.testPlatform === 'appstream' ?
                        (formik.values?.inputs ? (
                          <TestTemplateFileBrowser
                            testInputs={formik.values.inputs}
                            emptyFolders={formik.values.inputEmptyFolders}
                            onUpdate={handleInputUpdate}
                            selectedItem={selectedItem}
                            setSelectedItem={setSelectedItem}
                          />
                        ) : (
                          ""
                        )) : (
                          formik.values?.inputs?.length ? (
                            <Grid container alignItems="center" justifyContent="flex-start">
                              <Grid item xs={1}>
                                {formik.values.inputs[0].asset.isFolder ?
                                  <SnippetFolderIcon sx={{ color: 'gray' }} /> :
                                  <DescriptionIcon sx={{ color: 'gray' }} />}
                              </Grid>
                              <Grid item xs={8}>
                                <Typography variant="p">
                                  {formik.values.inputs[0].asset.filename}
                                </Typography>
                              </Grid>
                              <Grid item xs={1}>
                                <IconButton
                                  onClick={() => handleRemoveWebsiteInput()}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Grid>
                            </Grid>
                          ) : ""
                        )
                      }
                    </Grid>
                  </Grid>
                  <AddAssetForm
                    isAddAssetOpen={isUploadInstructionsOpen}
                    setIsAddAssetOpen={setIsUploadInstructionsOpen}
                    onUploadSuccess={handleInstructionsUploadSuccess}
                  />

                  <SelectAssetForm
                    isSelectAssetOpen={isSelectAssetOpen}
                    setIsSelectAssetOpen={setIsSelectAssetOpen}
                    onAssetSubmit={handleSelectedAssets}
                  />
                  <Grid item xs={12}>
                    <Grid container justifyContent="flex-end">
                      <Grid item>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleNext4}
                          sx={{ marginTop: 1 }}
                        >
                          {t("Continue")}
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </div>
              <div
                role="tabpanel"
                hidden={activeTab !== 4}
                id="tabpanel-4"
                aria-labelledby="tab-4"
              >
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography component="p">
                      {t("ApplicationsAvailableDuringTest")}
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <List>
                      {appsData
                        .sort((a, b) => {
                          if ((!a.disabled && b.disabled) ||
                            (a.testPlatform === formik.values.testPlatform &&
                              b.testPlatform !== formik.values.testPlatform)) {
                            return -1;
                          } else if ((a.disabled && !b.disabled) ||
                            (b.testPlatform === formik.values.testPlatform &&
                              a.testPlatform !== formik.values.testPlatform)) {
                            return 1;
                          } else {
                            return a.displayName
                              .toUpperCase()
                              .localeCompare(b.displayName.toUpperCase());
                          }
                        })
                        .map((r, i, apps) => {
                          const isAppSelected = formik.values.apps.some(app => app.id === r.id);
                          return (
                            <Fragment key={r.id}>
                              {(i > 0 && r.disabled && !apps[i - 1].disabled) &&
                                <div key={`premium-divider-${i}`}>
                                  <Divider orientation="horizontal" sx={{ backgroundColor: "darkgrey" }}
                                    key={`divider-${i}`}
                                  />
                                  <Typography key={`premium-text-${i}`} style={{ textTransform: 'uppercase' }}>
                                    {t("PremiumApplications")}
                                  </Typography>
                                </div>
                              }
                              <ListItem key={`list-item-${r.id}`}>
                                <Checkbox
                                  size="small"
                                  name="apps"
                                  value={r}
                                  onChange={(event) => handleAppSelectCheckboxChange(event, r)}
                                  checked={isAppSelected}
                                  disabled={r.testPlatform !== formik.values.testPlatform || r.disabled}
                                />
                                <ListItemAvatar>
                                  <Avatar
                                    alt={r.displayName}
                                    src={r.icon}
                                    variant="square"
                                    sx={{
                                      filter: (r.testPlatform !== formik.values.testPlatform || r.disabled) ?
                                        'grayscale(100%)' : 'none', // Apply grayscale filter when disabled
                                      opacity: (r.testPlatform !== formik.values.testPlatform || r.disabled) ?
                                        0.5 : 1 // Reduce opacity when disabled 
                                    }}
                                  />
                                </ListItemAvatar>
                                <ListItemText
                                  primary={r.displayName}
                                  secondary={
                                    r.developer && r.version
                                      ? r.developer + ", Version " + r.version
                                      : null
                                  }
                                />

                                <ListItemSecondaryAction>
                                  {isAppSelected && formik.values.testPlatform === 'appstream' && (
                                    <>
                                      <Typography component="span" style={{ marginRight: '10px' }}>
                                        {t("AutoStart")}
                                      </Typography>
                                      <Checkbox
                                        size="small"
                                        name="autoStart"
                                        onChange={(event) => handleAutoStartChange(event, r)}
                                        checked={formik.values.apps.find(app => app.id === r.id)?.autoStart || false}
                                      />
                                    </>
                                  )}
                                </ListItemSecondaryAction>
                              </ListItem>
                            </Fragment>
                          );
                        })}
                    </List>
                  </Grid>
                  <Grid item xs={12}>
                    <Grid container justifyContent="flex-end">
                      <Grid item>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleNext5}
                          sx={{ marginTop: 1 }}
                        >
                          {t("Continue")}
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </div>
              <div
                role="tabpanel"
                hidden={activeTab !== 5}
                id="tabpanel-5"
                aria-labelledby="tab-5"
              >
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Grid
                      container
                      alignItems="center"
                      justifyContent="flex-end"
                      spacing={2}
                    >
                      <Grid item xs={(formik.values.testPlatform === 'appstream') ? 10 : 12}>
                        <Typography component="p" sx={{ marginBottom: 3 }}>
                          {t("OutputsFromTest")}
                        </Typography>
                      </Grid>
                      {formik.values.testPlatform === 'appstream' &&
                        <Grid item xs={2}>
                          <Grid container justifyContent="flex-end">
                            <Grid item>
                              <Hidden xsDown>
                                <Button
                                  variant="outlined"
                                  onClick={handleAddOutput}
                                >
                                  {t("AddOutput")}
                                </Button>
                              </Hidden>
                              <Hidden smUp>
                                <IconButton onClick={handleAddOutput}>
                                  <AddIcon />
                                </IconButton>
                              </Hidden>
                            </Grid>
                          </Grid>
                        </Grid>
                      }
                    </Grid>
                    {formik.values.outputs.map((val, index) => (
                      <div style={{ marginBottom: "8px" }} key={index}>
                        <Grid item xs={12}>
                          <Grid container spacing={2} alignItems="center">
                            <Grid item xs={6}>
                              <TextField
                                variant="outlined"
                                fullWidth
                                id={"sequence" + index}
                                label={t("Sequence")}
                                name={`outputs.${index}.sequence`}
                                value={val.sequence}
                                onChange={formik.handleChange}
                                sx={{ display: "none" }}
                              />
                              <TextField
                                variant="outlined"
                                fullWidth
                                id={"name" + index}
                                label={t("Name")}
                                name={`outputs.${index}.name`}
                                value={val.name}
                                onChange={formik.handleChange}
                                // required
                                error={
                                  formik.touched?.outputs?.[index]?.name &&
                                  Boolean(formik.errors?.outputs?.[index]?.name)
                                }
                                helperText={
                                  formik.touched?.outputs?.[index]?.name &&
                                  formik.errors?.outputs?.[index]?.name
                                }
                              />
                            </Grid>
                            <Grid item xs={4}>
                              <FormControl
                                variant="outlined"
                                sx={{
                                  margin: 0,
                                  width: "100%",
                                  display: "flex",
                                  flexWrap: "nowrap",
                                }}
                              >
                                <InputLabel id={"extension" + index + "-label"}>
                                  {t("Extension")}
                                </InputLabel>
                                <Select
                                  label={t("Extension")}
                                  labelId={"extension" + index + "-label"}
                                  id={"extension" + index}
                                  name={`outputs.${index}.extension`}
                                  value={val.extension}
                                  onChange={formik.handleChange}
                                  // required
                                  error={
                                    formik.touched?.outputs?.[index]
                                      ?.extension &&
                                    Boolean(
                                      formik.errors?.outputs?.[index]?.extension
                                    )
                                  }
                                >
                                  <MenuItem key="directory" value="directory">
                                    Directory
                                  </MenuItem>
                                  {outputOptions}
                                </Select>
                                {formik.touched?.outputs?.[index]?.extension &&
                                  Boolean(
                                    formik.errors?.outputs?.[index]?.extension
                                  ) && (
                                    <FormHelperText error={true}>
                                      {
                                        formik.errors?.outputs?.[index]
                                          ?.extension
                                      }
                                    </FormHelperText>
                                  )}
                              </FormControl>
                            </Grid>
                            {formik.values.testPlatform === 'appstream' &&
                              <Grid item xs={2}>
                                <Grid container justifyContent="flex-end">
                                  <Grid item>
                                    <IconButton
                                      onClick={() => handleRemoveOutput(index)}
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </Grid>
                                </Grid>
                              </Grid>
                            }
                          </Grid>
                          {store.currentTestTemplate.testPlatform === 'appstream' &&
                            <Grid item xs={12}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    name={`outputs.${index}.autoSelect`}
                                    checked={val.autoSelect}
                                    onChange={formik.handleChange}
                                  />
                                }
                                label={t("AutoSelect")}
                              />
                              {val.autoSelect && (
                                <TextField
                                  variant="outlined"
                                  fullWidth
                                  id={"autoSelectPath" + index}
                                  label={t("AutoSelectPath")}
                                  name={`outputs.${index}.autoSelectPath`}
                                  value={val.autoSelectPath}
                                  onChange={formik.handleChange}
                                  InputProps={{
                                    startAdornment: <InputAdornment position="start">Test Files/</InputAdornment>
                                  }}
                                  error={
                                    formik.touched?.outputs?.[index]?.autoSelectPath &&
                                    Boolean(formik.errors?.outputs?.[index]?.autoSelectPath)
                                  }
                                  helperText={
                                    formik.touched?.outputs?.[index]?.autoSelectPath &&
                                    formik.errors?.outputs?.[index]?.autoSelectPath
                                  }
                                  sx={{ mb: "30px" }}
                                />
                              )}
                            </Grid>
                          }
                        </Grid>
                      </div>
                    ))}
                  </Grid>
                  <Grid item xs={12}>
                    <Grid container justifyContent="flex-end">
                      <Grid item>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleNext6}
                          sx={{ marginTop: 1 }}
                        >
                          {t("Continue")}
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </div>
              <div
                role="tabpanel"
                hidden={activeTab !== 6}
                id="tabpanel-6"
                aria-labelledby="tab-6"
              >
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography component="p">{t("RubricsTitle")}</Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formik.values.hasOverallScore}
                          onChange={formik.handleChange}
                          name="hasOverallScore"
                          color="primary"
                        />
                      }
                      label={t("HasOverallScore")}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formik.values.hasCategoryScore}
                          onChange={formik.handleChange}
                          name="hasCategoryScore"
                          color="primary"
                        />
                      }
                      label={t("HasCategoryScore")}
                    />
                  </Grid>

                  {formik.values.hasOverallScore ? (
                    <>
                      <Grid item xs={12}>
                        <Typography component="p" sx={{ marginBottom: 3 }}>
                          {t("OverallScoreRange")}
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Slider
                          value={formik.values.overallScoreRange}
                          onChangeCommitted={handleOverallScoreRangeChange}
                          name="overallScoreRange"
                          valueLabelDisplay="auto"
                          step={1}
                          marks
                          min={0}
                          max={10}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Typography component="p">
                          {t("OverallScoreDetails")}
                        </Typography>
                      </Grid>

                      {formik.values.overallScoreDetails.map((val, index) => (
                        <Grid item xs={12} key={index}>
                          <Grid container spacing={2} alignItems="center">
                            <Grid item xs={1}>
                              <TextField
                                variant="outlined"
                                fullWidth
                                id={"score" + index}
                                label={t("Score")}
                                value={val.score}
                                disabled
                                onChange={formik.handleChange}
                                name={`overallScoreDetails.${index}.score`}
                                // required
                                error={
                                  formik.touched?.overallScoreDetails?.[index]
                                    ?.score &&
                                  Boolean(
                                    formik.errors?.overallScoreDetails?.[index]
                                      ?.score
                                  )
                                }
                                helperText={
                                  formik.touched?.overallScoreDetails?.[index]
                                    ?.score &&
                                  formik.errors?.overallScoreDetails?.[index]
                                    ?.score
                                }
                              />
                            </Grid>
                            <Grid item xs={7}>
                              <TextField
                                variant="outlined"
                                fullWidth
                                id={"description" + index}
                                label={t("Description")}
                                name={`overallScoreDetails.${index}.description`}
                                multiline
                                required
                                value={val.description}
                                onChange={formik.handleChange}
                                // required
                                error={
                                  formik.touched?.overallScoreDetails?.[index]
                                    ?.description &&
                                  Boolean(
                                    formik.errors?.overallScoreDetails?.[index]
                                      ?.description
                                  )
                                }
                                helperText={
                                  formik.touched?.overallScoreDetails?.[index]
                                    ?.description &&
                                  formik.errors?.overallScoreDetails?.[index]
                                    ?.description
                                }
                              />
                            </Grid>
                            <Grid item xs={4}>
                              <TextField
                                variant="outlined"
                                fullWidth
                                id={"name" + index}
                                label={t("Name")}
                                name={`overallScoreDetails.${index}.name`}
                                value={val.name}
                                onChange={formik.handleChange}
                                // required
                                error={
                                  formik.touched?.overallScoreDetails?.[index]
                                    ?.name &&
                                  Boolean(
                                    formik.errors?.overallScoreDetails?.[index]
                                      ?.name
                                  )
                                }
                                helperText={
                                  formik.touched?.overallScoreDetails?.[index]
                                    ?.name &&
                                  formik.errors?.overallScoreDetails?.[index]
                                    ?.name
                                }
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                      ))}
                    </>
                  ) : (
                    ""
                  )}

                  {formik.values.hasCategoryScore ? (
                    <Grid item xs={12} sx={{ marginTop: 5 }}>
                      <Grid
                        container
                        alignItems="center"
                        justifyContent="flex-end"
                        spacing={2}
                      >
                        <Grid item xs={10}>
                          <Typography component="p" sx={{ marginBottom: 3 }}>
                            {t("Categories")}
                          </Typography>
                        </Grid>

                        <Grid item xs={2}>
                          <Grid
                            container
                            justifyContent="flex-end"
                            sx={{ marginBottom: 3 }}
                          >
                            <Grid item>
                              <Hidden xsDown>
                                <Button
                                  variant="outlined"
                                  onClick={handleAddCategory}
                                >
                                  {t("AddCategory")}
                                </Button>
                              </Hidden>
                              <Hidden smUp>
                                <IconButton onClick={handleAddCategory}>
                                  <AddIcon />
                                </IconButton>
                              </Hidden>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                      {formik.values.categories.map((val, index) => (
                        <div style={{ marginBottom: "8px" }} key={index}>
                          <Grid item xs={12}>
                            <Grid
                              container
                              spacing={2}
                              alignItems="center"
                              sx={{ marginBottom: 8 }}
                            >
                              <Grid item xs={10}>
                                <Grid container spacing={2}>
                                  <Grid item xs={12}>
                                    <TextField
                                      variant="outlined"
                                      fullWidth
                                      id={"csequence" + index}
                                      label={t("Sequence")}
                                      value={val.sequence}
                                      onChange={formik.handleChange}
                                      name={`categories.${index}.sequence`}
                                      sx={{ display: "none" }}
                                    />
                                  </Grid>

                                  <Grid item xs={12}>
                                    <TextField
                                      variant="outlined"
                                      fullWidth
                                      id={"cname" + index}
                                      label={t("Name")}
                                      name={`categories.${index}.name`}
                                      value={val.name}
                                      onChange={formik.handleChange}
                                      // required
                                      error={
                                        formik.touched?.categories?.[index]
                                          ?.name &&
                                        Boolean(
                                          formik.errors?.categories?.[index]
                                            ?.name
                                        )
                                      }
                                      helperText={
                                        formik.touched?.categories?.[index]
                                          ?.name &&
                                        formik.errors?.categories?.[index]?.name
                                      }
                                    />
                                  </Grid>

                                  <Grid item xs={12}>
                                    <TextField
                                      variant="outlined"
                                      fullWidth
                                      id={"cdescription" + index}
                                      label={t("Description")}
                                      name={`categories.${index}.description`}
                                      multiline
                                      value={val.description}
                                      onChange={formik.handleChange}
                                      // required
                                      error={
                                        formik.touched?.categories?.[index]
                                          ?.description &&
                                        Boolean(
                                          formik.errors?.categories?.[index]
                                            ?.description
                                        )
                                      }
                                      helperText={
                                        formik.touched?.categories?.[index]
                                          ?.description &&
                                        formik.errors?.categories?.[index]
                                          ?.description
                                      }
                                    />
                                  </Grid>

                                  <Grid item xs={12}>
                                    <Typography component="p">
                                      {t("CategoryScoreRange")}
                                    </Typography>
                                  </Grid>

                                  <Grid item xs={12}>
                                    <Slider
                                      value={val.scoreRange}
                                      onChange={(e) => {
                                        setCurrentCategory(index);
                                      }}
                                      onChangeCommitted={
                                        handleCategoryScoreRangeChange
                                      }
                                      name={`categories.${index}.scoreRange`}
                                      valueLabelDisplay="auto"
                                      step={1}
                                      marks
                                      min={0}
                                      max={10}
                                    />
                                  </Grid>

                                  <Grid item xs={12}>
                                    <TextField
                                      variant="outlined"
                                      fullWidth
                                      id={"cweight" + index}
                                      label={t("Weight")}
                                      name={`categories.${index}.weight`}
                                      value={val.weight}
                                      onChange={formik.handleChange}
                                      // required
                                      error={
                                        formik.touched?.categories?.[index]
                                          ?.weight &&
                                        Boolean(
                                          formik.errors?.categories?.[index]
                                            ?.weight
                                        )
                                      }
                                      helperText={
                                        formik.touched?.categories?.[index]
                                          ?.weight &&
                                        formik.errors?.categories?.[index]
                                          ?.weight
                                      }
                                    />
                                  </Grid>

                                  {formik.values.categories[
                                    index
                                  ].categoryScoreDetails.map((val, csindex) => (
                                    <Grid item xs={12} key={csindex}>
                                      <Grid
                                        container
                                        spacing={2}
                                        alignItems="center"
                                      >
                                        <Grid item xs={1}>
                                          <TextField
                                            variant="outlined"
                                            fullWidth
                                            id={"csscore" + csindex}
                                            label={t("Score")}
                                            value={val.score}
                                            disabled
                                            onChange={formik.handleChange}
                                            name={`categories.${index}.categoryScoreDetails.${csindex}.score`}
                                            // required
                                            error={
                                              formik.touched
                                                ?.categoryScoreDetails?.[
                                                csindex
                                              ]?.score &&
                                              Boolean(
                                                formik.errors
                                                  ?.categoryScoreDetails?.[
                                                  csindex
                                                ]?.score
                                              )
                                            }
                                            helperText={
                                              formik.touched
                                                ?.categoryScoreDetails?.[
                                                csindex
                                              ]?.score &&
                                              formik.errors
                                                ?.categoryScoreDetails?.[
                                                csindex
                                              ]?.score
                                            }
                                          />
                                        </Grid>
                                        <Grid item xs={7}>
                                          <TextField
                                            variant="outlined"
                                            fullWidth
                                            id={"csdescription" + csindex}
                                            label={t("Description")}
                                            name={`categories.${index}.categoryScoreDetails.${csindex}.description`}
                                            value={val.description}
                                            multiline
                                            required
                                            onChange={formik.handleChange}
                                            // required
                                            error={
                                              formik.touched
                                                ?.categoryScoreDetails?.[
                                                csindex
                                              ]?.description &&
                                              Boolean(
                                                formik.errors
                                                  ?.categoryScoreDetails?.[
                                                  csindex
                                                ]?.description
                                              )
                                            }
                                            helperText={
                                              formik.touched
                                                ?.categoryScoreDetails?.[
                                                csindex
                                              ]?.description &&
                                              formik.errors
                                                ?.categoryScoreDetails?.[
                                                csindex
                                              ]?.description
                                            }
                                          />
                                        </Grid>
                                        <Grid item xs={4}>
                                          <TextField
                                            variant="outlined"
                                            fullWidth
                                            id={"csname" + csindex}
                                            label={t("Name")}
                                            name={`categories.${index}.categoryScoreDetails.${csindex}.name`}
                                            value={val.name}
                                            onChange={formik.handleChange}
                                            // required
                                            error={
                                              formik.touched
                                                ?.categoryScoreDetails?.[
                                                csindex
                                              ]?.name &&
                                              Boolean(
                                                formik.errors
                                                  ?.categoryScoreDetails?.[
                                                  csindex
                                                ]?.name
                                              )
                                            }
                                            helperText={
                                              formik.touched
                                                ?.categoryScoreDetails?.[
                                                csindex
                                              ]?.name &&
                                              formik.errors
                                                ?.categoryScoreDetails?.[
                                                csindex
                                              ]?.name
                                            }
                                          />
                                        </Grid>
                                      </Grid>
                                    </Grid>
                                  ))}
                                </Grid>
                              </Grid>
                              <Grid item xs={2}>
                                <Grid container justifyContent="flex-end">
                                  <Grid item>
                                    <IconButton
                                      onClick={() =>
                                        handleRemoveCategory(index)
                                      }
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Grid>
                          </Grid>
                        </div>
                      ))}
                    </Grid>
                  ) : (
                    ""
                  )}

                  <Grid item xs={12}>
                    <Grid container justifyContent="flex-end">
                      <Grid item>
                        <Button
                          type="submit"
                          variant="contained"
                          color="primary"
                          sx={{ marginTop: 1 }}
                        >
                          {props.isCopyMode ? t("Copy") : t("Update")}
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </div>
            </Grid>
          </Grid>
        </Box>
        <Dialog
          open={confirmChangeTestPlatform}
          onClose={handleConfirmChangePlatformClose}
        >
          <ConfirmationForm
            handleConfirmationClose={handleConfirmChangePlatformClose}
            dialogTitle={t("ChangeTestPlatformTitle")}
            dialogContent={t("ChangeTestPlatformExplanation")}
          />
        </Dialog>
      </form>
    );
  } else {
    pageResults = "";
  }
  return pageResults;
}
